import { get } from 'svelte/store';
import { app, setGlobalStore } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import { ApiService } from '$lib/services/api';
import { getDeviceId } from '$lib/utils/device-id';
import * as Sentry from '@sentry/sveltekit';
import {
	AB_API_HOSTS,
	DEVICE_KEY_MAP,
	PROFILE,
	EXPERIMENT_KEYS,
	DATA_TYPES,
	DATA_KEYS,
} from '$lib/services/experiments/client/constants';
import { ProductExperienceService } from '$lib/services/product-experience';
import { AOFL_PRODUCT_NAMES } from '$lib/utils/constants';
import { browser } from '$app/environment';
import { page } from '$app/stores';
import { isbot } from 'isbot';
import {
	has1_0Account,
	has2_0Account,
	is1_0OriginatingUser,
	is2_0OriginatingUser,
} from '$lib/utils/aofl-accounts';

/**
 * Service for managing A/B testing experiments.
 */
class ExperimentService {
	/**
	 * The API host for the A/B testing service, based on the current environment.
	 * @returns {string} - The API host for the A/B testing service, based on the current environment.
	 */
	get AB_API_HOST() {
		const {
			global: { environment },
		} = get(app);
		return AB_API_HOSTS[environment];
	}

	/**
	 * Constructs the device data array for the API call.
	 * @returns {Array<object>} - An array of device data objects.
	 */
	getDeviceData() {
		const appMeta = get(app).meta;
		const appGlobal = get(app).global;
		const deviceId = getDeviceId();

		const appData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, metaKey]) => {
			if (appMeta[metaKey] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: deviceId,
					key,
					value: String(appMeta[metaKey]),
				},
			];
		});

		const globalData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, globalKey]) => {
			if (appGlobal[globalKey] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: deviceId,
					key,
					value: String(appGlobal[globalKey]),
				},
			];
		});

		return [...appData, ...globalData];
	}

	/**
	 * Constructs the user data object for the API call.
	 * @returns {object} - The user data object.
	 */
	getUserData() {
		const userStore = get(user);
		return {
			device_id: getDeviceId(),
			master_id: userStore.userToProductUuid || 'null',
			session_id: 'null',
			user_id: userStore.userToProductUuid || 'null',
		};
	}

	/**
	 * Builds subscription key object containing the user_subscription type value.
	 * @returns {{ user_subscription_type: string | null }}
	 */
	getSubscriptionMetadata() {
		const subscriptionData = this.getSubscriptionData();
		const userSubscription =
			subscriptionData?.find((item) => item.key === DATA_KEYS.USER_SUBSCRIPTION_TYPE)?.value ??
			null;
		return { user_subscription_type: userSubscription };
	}

	/**
	 * Constructs the subscription data object for the API call.
	 * @returns {Array<object>|null} - The subscription data object, or null if the user does not have a userToProductUuid.
	 */
	getSubscriptionData() {
		// User subscription status
		const userStore = get(user);
		if (!userStore.userToProductUuid) return null;

		const subscriptionStatus = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid,
			key: DATA_KEYS.SUBSCRIPTION_STATUS,
			value: userStore.accountStatus,
		};

		// User subscription type
		const is1_0Only = has1_0Account() && !has2_0Account();
		const is1_0OriginatingMixed = is1_0OriginatingUser() && has2_0Account();
		const is2_0Only = has2_0Account() && !has1_0Account();
		const is2_0OriginatingMixed = has2_0Account() && is2_0OriginatingUser();

		let userSubscriptionTypeValue = null;
		switch (true) {
			case is1_0Only:
				userSubscriptionTypeValue = '1.0_only';
				break;
			case is1_0OriginatingMixed:
				userSubscriptionTypeValue = '1.0_originating_mixed';
				break;
			case is2_0Only:
				userSubscriptionTypeValue = '2.0_only';
				break;
			case is2_0OriginatingMixed:
				userSubscriptionTypeValue = '2.0_originating_mixed';
				break;
			default:
				userSubscriptionTypeValue = null;
		}

		const userSubscriptionType = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid,
			key: DATA_KEYS.USER_SUBSCRIPTION_TYPE,
			value: userSubscriptionTypeValue,
		};

		const isReactivated = userStore.isReactivated;
		const reactivatedUser = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid,
			key: DATA_KEYS.REACTIVATED_USER,
			value: isReactivated ? 'true' : 'false',
		};

		const isFromSpecialOfferPage = userStore.userMetaData?.special_offer;
		const specialOfferUser = {
			type: DATA_TYPES.MASTER,
			id: userStore.userToProductUuid,
			key: DATA_KEYS.SPECIAL_OFFER_USER,
			value: isFromSpecialOfferPage ? 'true' : 'false',
		};

		return [subscriptionStatus, userSubscriptionType, reactivatedUser, specialOfferUser];
	}

	/**
	 * Fetches experiments with a given payload, updates the store, and returns the results.
	 * @param {object} payload - The payload for the getExperiments API call.
	 * @example
	 * const payload = {
	 *   user: {
	 *     device_id: 'some-device-id',
	 *     session_id: 'some-session-id',
	 *     user_id: 'some-user-id',
	 *     master_id: 'some-master-id'
	 *   },
	 *   experiment_keys: ['exp_key_1', 'exp_key_2'],
	 *   profile: 'default',
	 *   data: [
	 *     {
	 *       type: 'device',
	 *       id: 'some-device-id',
	 *       key: 'platform',
	 *       value: 'web'
	 *     }
	 *   ]
	 * };
	 * @returns {Promise<void|undefined>} - A promise that resolves when the store is updated.
	 */
	async getExperiments(payload) {
		try {
			const response = await ApiService.getExperiments(this.AB_API_HOST, payload);
			const newExperiments = response?.results;

			if (!newExperiments) {
				await setGlobalStore({ experiments: [] });
				return;
			}

			const {
				global: { experiments: oldExperiments },
			} = get(app);
			const seenState = new Map();
			if (oldExperiments) {
				for (const exp of oldExperiments) {
					if (exp.seen) {
						seenState.set(exp.key, true);
					}
				}
			}

			const experimentsForStore = newExperiments.map((exp) => ({
				...exp,
				seen: seenState.has(exp.key) || false,
			}));

			const newKeys = new Set(experimentsForStore.map((exp) => exp.key));
			const mergedExperiments = [
				...(oldExperiments || []).filter((exp) => !newKeys.has(exp.key)),
				...experimentsForStore,
			];

			await setGlobalStore({ experiments: mergedExperiments });
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Failed To Get Experiments',
				},
			});
		}
	}

	/**
	 * Initializes the experiments for the current user/device on app load.
	 * @param {string[]} experimentKeys - The keys of the experiments to initialize.
	 * @returns {Promise<void|undefined>} - A promise that resolves when the experiments have been initialized.
	 */
	async initializeExperiments(experimentKeys = EXPERIMENT_KEYS) {
		const userAgent = navigator?.userAgent ?? '';

		// The bot check is mischaracterizing our custom user-agents from unity as bot traffic and since bot
		// traffic is not primarily known to be coming from in-app - it's safe to skip it for in-app users
		if (isbot(userAgent) && !get(app).meta.inApp) {
			const existingExperiments = get(app).global.experiments;
			if (!existingExperiments || existingExperiments.length === 0) {
				const defaultExperiments = experimentKeys.map((key) => ({
					key,
					variant: 'control_variant',
					seen: false,
					assigned: false,
					experiment_id: null,
				}));
				await setGlobalStore({ experiments: defaultExperiments });
				return;
			}

			Sentry.captureMessage('ExperimentsClient: bot session detected', {
				level: 'warning',
				extra: { user_agent: userAgent },
			});
			return;
		}

		const subscriptionData = this.getSubscriptionData();
		const deviceData = this.getDeviceData();
		const data = [...deviceData];
		if (subscriptionData) data.push(...subscriptionData);

		return this.getExperiments({
			user: this.getUserData(),
			experiment_keys: experimentKeys,
			profile: PROFILE,
			data,
			auto_mark_seen: false,
		});
	}

	/**
	 * Gets the assigned variant for a specific experiment.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {{page_url: string, page_name: string, product_hashes?: Record<string, string>}} pageMetadata - The page metadata to be sent to the API. `product_hashes` (map of products visible on the page, e.g. `{monthly, annual}` or `{primary, alternate}`) are optional and, when supplied, are forwarded as targeting/segmentation attributes.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<string|null>} - The assigned variant for the experiment, or null if the experiment is not found.
	 */
	async getExperimentArm(experimentKey, pageMetadata, fireEvent = true) {
		const {
			global: { experiments },
			meta,
		} = get(app);

		if (!experiments || experiments.length === 0 || !experimentKey) return null;

		const experiment = experiments.find((exp) => exp.key === experimentKey);
		const assignedVariant = experiment?.variant || null;

		if (browser) {
			// @ts-ignore
			pageMetadata.navigator = window.navigator.userAgent;
			// @ts-ignore
			pageMetadata.window_url = window.location.href;
			// @ts-ignore
			pageMetadata.svelte_page_url = get(page).url;
			// @ts-ignore
			pageMetadata.is_tablet = meta.isTabletViewer;
			// @ts-ignore
			pageMetadata.is_desktop = meta.isDesktopViewer;
		}

		if (fireEvent && experiment?.experiment_id && !experiment?.seen) {
			const payload = {
				...experiment,
				profile: PROFILE,
				user: this.getUserData(),
				pageMetadata: pageMetadata,
				subscriptionMetadata: this.getSubscriptionMetadata(),
			};

			try {
				await ApiService.markExperimentSeen(this.AB_API_HOST, payload);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Failed To Mark Experiment Seen',
						payload,
					},
				});
			}

			experiment.seen = true;
			await setGlobalStore({ experiments });
		}

		if (assignedVariant?.includes('test')) {
			ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		}
		return assignedVariant;
	}

	/**
	 * Checks if a specific experiment variant is active and marks the experiment as seen.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {string} argVariant - The client variant used to check against the assigned variant.
	 * @param {{page_url: string, page_name: string, product_hashes?: Record<string, string>}} pageMetadata - The page metadata to be sent to the API. `product_hashes` (map of products visible on the page, e.g. `{monthly, annual}` or `{primary, alternate}`) are optional and, when supplied, are forwarded as targeting/segmentation attributes.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<boolean>} - True if the experiment variant is active, false otherwise.
	 */
	async isExperimentArmActive(experimentKey, argVariant, pageMetadata, fireEvent = true) {
		const assignedVariant = await this.getExperimentArm(experimentKey, pageMetadata, fireEvent);
		return assignedVariant === argVariant;
	}

	/**
	 * Synchronously checks if a specific experiment variant is active by reading from the store.
	 * Use this when you need to check experiment state without async/await (no "seen" event will be fired).
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {string} variant - The variant to check against.
	 * @returns {boolean} - True if the experiment variant is active, false otherwise.
	 */
	isExperimentArmActiveSync(experimentKey, variant) {
		const {
			global: { experiments },
		} = get(app);

		if (!experiments || experiments.length === 0 || !experimentKey) return false;

		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant === variant;
	}

	/**
	 * Checks if any of the given variants are active for a specific experiment.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {string[]} variants - The variants to check against.
	 * @param {{page_url: string, page_name: string, product_hashes?: Record<string, string>}} pageMetadata - The page metadata to be sent to the API. `product_hashes` (map of products visible on the page, e.g. `{monthly, annual}` or `{primary, alternate}`) are optional and, when supplied, are forwarded as targeting/segmentation attributes.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<boolean>} - True if any of the variants are active, false otherwise.
	 */
	async isAnyExperimentArmActive(experimentKey, variants, pageMetadata, fireEvent = true) {
		const assignedVariant = await this.getExperimentArm(experimentKey, pageMetadata, fireEvent);
		if (!assignedVariant) return false;
		return variants.includes(assignedVariant);
	}

	/**
	 * Checks if at least one of the provided experiment keys is assigned.
	 * @param {string[]} experimentKeys - The experiment keys to check.
	 * @returns {boolean} - True if any of the given experiments are assigned, false otherwise.
	 */
	isAnyExperimentAssigned(experimentKeys) {
		const {
			global: { experiments },
		} = get(app);

		if (!Array.isArray(experimentKeys) || experimentKeys.length === 0) return false;
		if (!experiments || experiments.length === 0) return false;

		// TODO: String check can be removed once experiment service persists assigned value across calls
		for (const key of experimentKeys) {
			const experiment = experiments.find((exp) => exp.key === key);
			if (experiment?.assigned || typeof experiment?.experiment_id === 'string') return true;
		}

		return false;
	}

	/**
	 * Marks an experiment as seen.
	 * @param {string} experimentKey - The key of the experiment to mark as seen.
	 * @param {{page_url: string, page_name: string, product_hashes?: Record<string, string>}} pageMetadata - The page metadata to be sent to the API. `product_hashes` (map of products visible on the page, e.g. `{monthly, annual}` or `{primary, alternate}`) are optional and, when supplied, are forwarded as targeting/segmentation attributes.
	 * @returns {Promise<void>} - A promise that resolves when the experiment has been marked as seen.
	 */
	async markExperimentAsSeen(experimentKey, pageMetadata) {
		const {
			global: { experiments },
		} = get(app);

		const experiment = experiments.find((exp) => exp.key === experimentKey);
		if (!experiment) return;

		if (experiment.experiment_id && !experiment.seen) {
			const payload = {
				...experiment,
				profile: PROFILE,
				user: this.getUserData(),
				pageMetadata,
				subscriptionMetadata: this.getSubscriptionMetadata(),
			};

			try {
				await ApiService.markExperimentSeen(this.AB_API_HOST, payload);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Failed To Mark Experiment Seen',
						payload,
					},
				});
			}

			experiment.seen = true;
			await setGlobalStore({ experiments });
		}
	}
}

export const experimentService = new ExperimentService();
